"use client"

import { nanoid } from "nanoid"
import pLimit from "p-limit"
import { useEffect } from "react"
import { action_getCheckoutProducts } from "src/actions/checkout"
import { mapCart } from "src/checkout/helpers/cart"
import { resetExpressCheckout } from "src/checkout/state/checkoutExpressStore"
import { setCartSidebarVisible } from "src/components/np-design/sections/cart-sidebar/state"
import { ResolvedListProductData } from "src/data/product-list-resolved/types"
import {
  wcAddItemToCartOrThrow,
  wcApplyCouponOrThrow,
  wcDeleteItemFromCart,
  wcEditCartItemQuantity,
  wcGetCart,
  wcRemoveCoupon,
  wcUpdateCartAddressesOrThrow,
} from "src/data/woocommerce/wcStoreApi"
import { WCStoreCart, WCUpdateUserAddressRequestData } from "src/data/woocommerce/wcStoreTypes"
import { createAsyncGlobalState } from "src/hooks/asyncGlobalState"
import { StorefrontCart } from "src/types/storefront"
import { recordError } from "src/utilities/errorReporting"
import { createMap } from "src/utilities/lookup"
import { create as createStore } from "zustand"
import { setGlobalErrorMessage } from "../../components/error/state"
import { trackAddToCart, trackCouponApplied, trackRemoveFromToCart } from "../../tracking/events"
import { extractWcErrorMessage } from "../helpers/wooCommerceErrors"
import { CheckoutStep, setCheckoutStep } from "../state/checkoutStageStore"

const checkoutProductsStore = createAsyncGlobalState({
  initialize: () => action_getCheckoutProducts(),
  initialValue: [],
})

export const useCheckoutProducts = checkoutProductsStore.use

export type CartUpdateJob = (cart: StorefrontCart) => Promise<StorefrontCart>

export type CartState = {
  data: StorefrontCart | null
  loading: boolean
  updating: boolean
}

const defaultState: CartState = {
  data: null,
  loading: true,
  updating: false,
}

const store = createStore(() => defaultState)

export function getCart() {
  return store.getState().data
}

export function useCartState() {
  // Initial fetch
  useEffect(() => {
    const state = store.getState()

    if (!state.updating && !state.data) {
      void restoreCart()
    }
  }, [])

  return store()
}

let cachedProducts: Map<string, ResolvedListProductData> | null = null

async function enrichCart(wcCart: WCStoreCart) {
  if (!cachedProducts || wcCart.items.some((item) => !cachedProducts?.has(item.sku))) {
    const products = await checkoutProductsStore.promise()
    const productsBySku = createMap(products, (product) => product.sku)

    cachedProducts = productsBySku
  }

  return mapCart(wcCart, cachedProducts)
}

const runAfterPreviousActions = pLimit(1)

async function restoreCart() {
  try {
    store.setState({ loading: true, updating: true })
    const newCart = await runAfterPreviousActions(() => wcGetCart().then(enrichCart))
    store.setState({ loading: false, updating: false, data: newCart })
  } catch (error) {
    recordError("Failed to load cart", error)
    store.setState({ loading: false, updating: false })
  }
}

async function processRawCartUpdate(
  id: string,
  updateCart: (cart: StorefrontCart) => Promise<WCStoreCart>,
): Promise<StorefrontCart> {
  console.debug("🧺", id, "processRawCartUpdate", updateCart)

  console.debug("🧺", id, "set updating")
  const state = store.getState()

  if (!state.updating) {
    store.setState({ loading: !state.data, updating: true })
  }

  console.debug("🧺", id, "reset state")
  setGlobalErrorMessage(null)
  resetExpressCheckout()

  console.debug("🧺", id, "get old cart")
  const oldCart: StorefrontCart = state.data || (await wcGetCart().then(enrichCart))

  console.debug("🧺", id, "update cart")
  const newCart = await updateCart(oldCart).then(enrichCart)

  console.log(JSON.stringify(newCart, null, 2))

  console.debug("🧺", id, "set finished")
  store.setState({ loading: false, updating: false, data: newCart })

  return newCart
}

async function queueRawCartUpdate(updateCart: (cart: StorefrontCart) => Promise<WCStoreCart>): Promise<StorefrontCart> {
  const id = nanoid().slice(0, 4)
  console.debug("🧺", id, "queueRawCartUpdate", updateCart)

  console.debug("🧺", id, "await previous cart actions")
  return runAfterPreviousActions(() => processRawCartUpdate(id, updateCart))
}

async function queueManagedCartUpdate(
  updateCart: (oldCart: StorefrontCart) => Promise<WCStoreCart>,
): Promise<StorefrontCart> {
  try {
    return await queueRawCartUpdate(updateCart)
  } catch (error) {
    const { errorMessage, isFallback } = extractWcErrorMessage(error)

    setGlobalErrorMessage(errorMessage)

    if (isFallback) {
      recordError("Failed to perform cart action", error)
    }

    store.setState({ loading: false, updating: false })
    return getCart()!
  }
}

export async function addItemToCart(productId: number) {
  setCheckoutStep(CheckoutStep.Cart)

  const newCart = await queueManagedCartUpdate(() => wcAddItemToCartOrThrow(productId))

  const newItem = newCart.items.find((item) => item.id === productId)
  const quantityChange = 1

  if (newItem) {
    // The item is undefined if it had to be auto-removed from the cart because
    // it is no longer sold.
    trackAddToCart(newCart, newItem, quantityChange)
  }

  setCartSidebarVisible(true)

  return newCart
}

export async function removeItemFromCart(key: string) {
  const oldCart = getCart()
  const oldItem = oldCart?.items.find((item) => item.key === key)

  const newCart = await queueManagedCartUpdate(() => wcDeleteItemFromCart(key))

  if (newCart && oldItem) {
    const quantityChange = oldItem.quantity

    trackRemoveFromToCart(newCart, oldItem, quantityChange)
  }

  return newCart
}

export async function editCartItemQuantity(key: string, targetQuantity: number) {
  const oldCart = getCart()
  const oldItem = oldCart?.items.find((item) => item.key === key)

  if (!oldItem) {
    recordError("Failed to find item to edit in cart", { key, targetQuantity })
    return
  }

  const newCart = await queueManagedCartUpdate(() => wcEditCartItemQuantity(key, targetQuantity))

  const newItem = newCart?.items.find((item) => item.key === key)
  const quantityChange = (newItem?.quantity || 0) - oldItem.quantity

  if (newCart && newItem) {
    if (quantityChange > 0) {
      trackAddToCart(newCart, newItem, quantityChange)
    } else {
      trackRemoveFromToCart(newCart, newItem, -quantityChange)
    }
  }

  return newCart
}

export async function applyCouponOrThrow(code: string) {
  const newCart = queueRawCartUpdate(() => wcApplyCouponOrThrow(code))

  trackCouponApplied(code)

  return newCart
}

export async function removeCouponOrThrow(code: string) {
  await queueRawCartUpdate(() => wcRemoveCoupon(code))
}

export async function updateCartAddressesOrThrow(requestData: WCUpdateUserAddressRequestData) {
  return queueRawCartUpdate(() => wcUpdateCartAddressesOrThrow(requestData))
}
